// SurfaceIcons — line-art icons for the three blueprint editors.
//
// WHY SVG AND NOT A UNICODE GLYPH
// The rail started out with text glyphs, and the two that were objected to failed
// for reasons a glyph can't fix:
//
//   • '⚡' (U+26A1) carries default EMOJI presentation. Browsers render it from a
//     colour emoji font, so it ignored the rail's `color` completely — permanently
//     orange, unmoved by hover or by the active state that tints every sibling. It
//     read as a sticker on a row of line icons because that is what it was.
//   • '☺' was a cartoon among geometric marks, and the Unicode face repertoire
//     offers no line-art alternative.
//
// A glyph's weight, size and colour are whatever the font decides. These are drawn
// with `stroke="currentColor"`, so they inherit the rail's colour and every state
// change for free — the exact property the bolt could never have.
//
// ── DESIGN: SILHOUETTE, NOT DETAIL ───────────────────────────────────────────
// The first version of this file wrapped all three in a shared window frame — a
// rounded rect with a title bar and three dots — and distinguished them by a small
// mark inside. That was wrong, and obviously so once rendered: at ~17px the frame
// IS the icon and the mark is four pixels of mush, so the three buttons read as
// three identical windows.
//
// At this size only the OUTLINE carries information. So the frame is gone and each
// icon owns the whole box with a deliberately different silhouette:
//
//     body    a figure in A-pose             — a wide X of limbs, head detached
//     face    a head in outline              — one closed rounded mass
//     events  a chevron pair around a slash  — wide, spiky, no closed area
//
// Body and face are a PAIR and their icons have to say which part of the character
// they edit. The body button was a node graph for a while — accurate (it is clips
// wired into a state machine) and still wrong next to a face, because the two then
// read as "graph and face". Wiring is the event graph's subject, and `</>` already
// says it.
//
// Those are told apart by shape alone, at a glance, before any interior detail is
// legible — which is the only test that matters on a 40px rail.
//
// Sizing defaults to EM, not px. The rail sets
// `font-size: calc(24px * var(--aos-icon-scale, 1))` on the button and every text
// glyph inherits it, so an em-relative icon keeps following the Display panel's
// icon-size setting exactly as the glyphs it replaces did. A hard-coded px size
// would quietly opt these three buttons out of that control.

import React from 'react'

/** 0.86em of a 24px button ≈ 20px. Larger than the 0.72em first try: without a
 *  frame there is no outer edge doing the work, so the shape itself has to fill
 *  the button to carry the same weight as the text glyphs above it. */
const ICON_EM = '0.86em'

/** Shared stroke setup. `currentColor` is the whole point — see the header. */
const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

function Svg({ size = ICON_EM, children }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      // Decorative: the button already carries `title` and an accessible name, so
      // announcing the icon too would just repeat it.
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', flex: 'none' }}
    >
      {children}
    </svg>
  )
}

/**
 * Body graph — a figure in A-pose.
 *
 * It was a triangle of wired nodes, on the reasoning that the body blueprint IS
 * clips wired into a state machine. True, and beside the point: its SIBLING is a
 * face, so the pair read as "graph and face" rather than "body and face", and the
 * one thing the button has to say first is WHICH PART OF THE CHARACTER it edits
 * (Andres, on the rail). The event graph is the surface whose subject really is
 * wiring, and it already owns that idea with `</>`.
 *
 * A-pose rather than a plain stick figure because that is the rig's own rest pose
 * — apt here, and it spreads the limbs so the silhouette stays open at 17px where
 * arms held close to the body merge into the torso.
 */
export function BodyGraphIcon({ size = ICON_EM }) {
  return (
    <Svg size={size}>
      <circle cx="12" cy="4.6" r="2.5" {...STROKE} />
      {/* Spine. The head is a separate mass, so the gap at the neck is what keeps
          this from reading as one blob at small sizes. */}
      <line x1="12" y1="7.4" x2="12" y2="14.2" {...STROKE} />
      {/* Arms, down-and-out. */}
      <line x1="12" y1="9.4" x2="5.8" y2="13.6" {...STROKE} />
      <line x1="12" y1="9.4" x2="18.2" y2="13.6" {...STROKE} />
      {/* Legs, splayed to the same angle so the whole figure is one wide X. */}
      <line x1="12" y1="14.2" x2="7.6" y2="21.2" {...STROKE} />
      <line x1="12" y1="14.2" x2="16.4" y2="21.2" {...STROKE} />
    </Svg>
  )
}

/**
 * Face graph — a head in outline.
 *
 * One closed rounded mass, which is the opposite silhouette to the other two: no
 * spokes, no spikes. Drawn as a head that TAPERS to a chin rather than a circle,
 * and with a flat mouth line — a circle plus a curved mouth is a smiley, which is
 * what was objected to about '☺'.
 */
export function FaceGraphIcon({ size = ICON_EM }) {
  return (
    <Svg size={size}>
      {/* Cranium wide at the temples, narrowing to a rounded jaw. */}
      <path
        d="M12 2.6c4.3 0 6.9 2.9 6.9 7 0 1.9-.3 3.7-1 5.3-1 2.5-3.2 4.4-5.9 4.4s-4.9-1.9-5.9-4.4c-.7-1.6-1-3.4-1-5.3 0-4.1 2.6-7 6.9-7Z"
        {...STROKE}
      />
      <circle cx="9.4" cy="10.2" r="0.95" fill="currentColor" />
      <circle cx="14.6" cy="10.2" r="0.95" fill="currentColor" />
      {/* Flat, deliberately. A curve here is a smile. */}
      <line x1="9.9" y1="14.4" x2="14.1" y2="14.4" {...STROKE} />
      {/* Neck, so the mass reads as a head rather than an egg. */}
      <line x1="12" y1="19.3" x2="12" y2="21.4" {...STROKE} />
    </Svg>
  )
}

/**
 * Event graph — `</>`, the motif from the reference.
 *
 * Apt rather than borrowed: the event graph IS the scripting layer, the one
 * surface where you wire logic instead of blending animation. Full-width and
 * spiky, with no enclosed area at all — the most distinct of the three at any size.
 */
export function EventGraphIcon({ size = ICON_EM }) {
  return (
    <Svg size={size}>
      <polyline points="8.2,6.6 2.6,12 8.2,17.4" {...STROKE} />
      <polyline points="15.8,6.6 21.4,12 15.8,17.4" {...STROKE} />
      <line x1="13.6" y1="5.4" x2="10.4" y2="18.6" {...STROKE} />
    </Svg>
  )
}

/**
 * Frame avatar — a crosshair that fills the box.
 *
 * Replaces the '⌖' text glyph, which ships tiny ink coverage at any font
 * size (the ring is a fraction of the em square), so the button read as
 * visibly smaller than every sibling — called out by Andres 2026-08-13.
 * Same reason as the header: an SVG owns its geometry, a glyph's is
 * whatever the font decided.
 */
export function FrameIcon({ size = ICON_EM }) {
  return (
    <Svg size={size}>
      <circle cx="12" cy="12" r="6.2" {...STROKE} />
      <line x1="12" y1="1.8" x2="12" y2="5.6" {...STROKE} />
      <line x1="12" y1="18.4" x2="12" y2="22.2" {...STROKE} />
      <line x1="1.8" y1="12" x2="5.6" y2="12" {...STROKE} />
      <line x1="18.4" y1="12" x2="22.2" y2="12" {...STROKE} />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" />
    </Svg>
  )
}

/**
 * Parallax view mode — an isometric cube.
 *
 * The universal "3D" mark: a hexagon silhouette with three interior edges,
 * unlike anything else on the rail (rings, head, chevrons, crosshair,
 * dotted square). The feature it toggles (head-tracked parallax, ENG-981)
 * is exactly "the viewport becomes 3D", so the cube says what the mode does
 * rather than how it's implemented.
 */
export function ParallaxIcon({ size = ICON_EM, blocked = false }) {
  return (
    <Svg size={size}>
      <path d="M12 2.4 20.4 7.2 20.4 16.8 12 21.6 3.6 16.8 3.6 7.2 Z" {...STROKE} />
      <line x1="12" y1="12" x2="20.4" y2="7.2" {...STROKE} />
      <line x1="12" y1="12" x2="3.6" y2="7.2" {...STROKE} />
      <line x1="12" y1="12" x2="12" y2="21.6" {...STROKE} />
      {/* Blocked/error: the camera-off slash, corner to corner over the cube —
          the same visual grammar as every muted-mic / video-off icon. Slightly
          heavier than the cube's stroke so it reads as ON TOP of the shape
          rather than another cube edge. Colour rides currentColor like
          everything else, so the button's danger tint makes it red. */}
      {blocked && <line x1="2.6" y1="2.6" x2="21.4" y2="21.4" {...STROKE} strokeWidth={2.2} />}
    </Svg>
  )
}

/**
 * Brain — the conversation workspace (convorcher: persona, stories, cached
 * responses, prefab triggers).
 *
 * A bumpy symmetric mass split by a central fissure. Deliberately the opposite
 * reading to FaceGraphIcon's smooth head: that one is ONE closed rounded
 * outline with facial features, this one is a wide lobed blob whose only
 * interior mark is the vertical midline — told apart by silhouette alone at
 * rail size, per the header's rule.
 */
export function BrainIcon({ size = ICON_EM }) {
  return (
    <Svg size={size}>
      {/* Two hemispheres as one lobed outline — each cubic run bulges past the
          hull, so the edge stays visibly bumpy even at ~17px. */}
      <path
        d="M12 4
           C10.8 2.9 8.9 2.9 7.8 4
           C6.1 3.9 4.7 5.2 4.6 6.9
           C3.2 7.7 2.7 9.5 3.5 10.9
           C2.9 12.4 3.5 14.1 4.9 14.8
           C5 16.6 6.5 17.9 8.3 17.8
           C9 19.3 10.9 19.9 12 19
           C13.1 19.9 15 19.3 15.7 17.8
           C17.5 17.9 19 16.6 19.1 14.8
           C20.5 14.1 21.1 12.4 20.5 10.9
           C21.3 9.5 20.8 7.7 19.4 6.9
           C19.3 5.2 17.9 3.9 16.2 4
           C15.1 2.9 13.2 2.9 12 4 Z"
        {...STROKE}
      />
      {/* The longitudinal fissure — the one interior mark that survives 17px. */}
      <line x1="12" y1="4" x2="12" y2="19" {...STROKE} />
      {/* One gyrus hint per hemisphere; detail, not load-bearing. */}
      <path d="M7 8.4 c1 .3 1.7 1.1 1.9 2.1" {...STROKE} />
      <path d="M17 8.4 c-1 .3 -1.7 1.1 -1.9 2.1" {...STROKE} />
    </Svg>
  )
}

/**
 * Version control — the git branch mark.
 *
 * Replaces the '⏇' glyph, which is U+23C7 (a dictation/record symbol) and reads
 * as a bare T — it says nothing about version control to anyone (Andres, on the
 * rail). A trunk with a branch splitting off it is the universally understood
 * mark for this: Git's own, GitHub's octicon, and every editor's SCM panel use
 * the same three-dot-and-hook shape, so it is recognised before it is read.
 *
 * Three dots and two strokes survive 17px; the commit dots are filled so they
 * stay solid when the strokes go sub-pixel.
 */
export function VersionControlIcon({ size = ICON_EM }) {
  return (
    <Svg size={size}>
      {/* Trunk. */}
      <line x1="7" y1="7.2" x2="7" y2="16.8" {...STROKE} />
      <circle cx="7" cy="4.6" r="2.4" {...STROKE} />
      <circle cx="7" cy="19.4" r="2.4" {...STROKE} />
      {/* The branch: down from its own commit, then a quarter turn into the
          trunk — the "diverges and rejoins" reading in one stroke. */}
      <circle cx="17" cy="4.6" r="2.4" {...STROKE} />
      <path d="M17 7 v2.6 a3.4 3.4 0 0 1 -3.4 3.4 H7" {...STROKE} />
    </Svg>
  )
}

/** surface id → icon component, mirroring SURFACE_LABELS / SURFACE_GLYPH. */
export const SURFACE_ICON = Object.freeze({
  body: BodyGraphIcon,
  face: FaceGraphIcon,
  events: EventGraphIcon,
})

/** Render the icon for a surface id, or null for an unknown one. */
export function SurfaceIcon({ surface, size = ICON_EM }) {
  const Icon = SURFACE_ICON[surface]
  return Icon ? <Icon size={size} /> : null
}

export default SurfaceIcon
